import React from 'react';
import { Modal, Card, Heading, Text } from './ui';
import './IOSAppDialog.css';

/**
 * IOSAppDialog — promo/splash modal that drives iOS app download.
 *
 * Public props (unchanged): isOpen, onClose, type ('project' | 'room')
 *
 * This dialog uses the Modal primitive for portal, ESC, scrim, focus-trap,
 * and scroll-lock — but opts OUT of the Modal's built-in header by passing
 * showClose={false} and no title. The marketing-style layout (centered
 * icon → title → body → features card → download CTA + QR) is rendered
 * inside the Modal's body slot, with a floating close button overlaid on
 * the top-right of the dialog content.
 */
const IOSAppDialog = ({ isOpen, onClose, type = 'project' }) => {
  const isProject = type === 'project';
  const title = isProject ? 'Create New Project' : 'Create New Room Scan';
  const description = isProject
    ? "To create a new project, you'll need to use the Housfully iOS app. The app allows you to capture and upload 3D scans of your spaces directly from your iPhone or iPad."
    : "To create a new room scan, you'll need to use the Housfully iOS app. The app uses your device's camera and sensors to capture detailed 3D scans of your rooms.";

  return (
    <Modal open={isOpen} onClose={onClose} size="md" showClose={false}>
      <div className="ios-dialog">
        <button
          type="button"
          className="ios-dialog__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="ios-dialog__icon-wrap">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
            <line x1="12" y1="18" x2="12" y2="18" />
          </svg>
        </div>

        <Heading level={2} className="ios-dialog__title">
          {title}
        </Heading>

        <Text className="ios-dialog__body">{description}</Text>

        <Card variant="flat" padding="md" className="ios-dialog__features">
          <Heading level={3} className="ios-dialog__features-title">
            App Features:
          </Heading>
          <ul className="ios-dialog__features-list">
            <li>LiDAR-powered 3D scanning</li>
            <li>Real-time preview</li>
            <li>Automatic room detection</li>
            <li>Cloud synchronization</li>
          </ul>
        </Card>

        <Heading level={3} className="ios-dialog__download-title">
          Download Housfully for iOS
        </Heading>

        <a
          href="https://apple.co/4869ZNT"
          target="_blank"
          rel="noopener noreferrer"
          className="ios-dialog__store-btn"
        >
          <img
            src="/app-store-badge.svg"
            alt="Download on the App Store"
            className="ios-dialog__store-badge"
          />
        </a>

        <div className="ios-dialog__qr">
          <img
            src="/app-store-qr.png"
            alt="QR Code - Scan to download"
            className="ios-dialog__qr-image"
          />
          <Text size="sm" className="ios-dialog__qr-caption">
            Scan to download
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default IOSAppDialog;
